import { useCallback } from 'react';

import { useSelector } from 'react-redux';

import type { PackableClaims } from '@src/packages/utils/access';
import { hasAnyAllowedRole, isWriteRoleList, resolveOrgAccess } from '@src/packages/utils/access';

import { useAccountWritable } from './useAccountStatus';

interface DivisionAccess {
  access?: string;
}

interface AccessState {
  organizations?: { organization?: { id?: string } };
  user?: {
    access?: { data?: { claims?: PackableClaims } };
    orgUser?: { data?: { access?: string } };
  };
  divisions?: { assigned?: { data?: { divisions?: Record<string, DivisionAccess> } } };
}

/**
 * A single id asks for the role on that division. A **list** asks for the role on *every* division
 * in it, which is what the backend demands to write a record spanning several divisions
 * (`orgOrDivisionWritePolicy` and `assertStoredDivisionAccess` both check all of them). An empty
 * list is org-wide: division roles grant nothing, only the organization role counts.
 */
export type DivisionScope = string | readonly string[] | null;

export type AllowedRoleChecker = (
  allowedRoles?: readonly string[] | null,
  divScope?: DivisionScope,
) => boolean;

/**
 * Returns a predicate with the current user's roles already resolved, so a list of
 * items can be filtered without calling a hook per item. When a division scope is given, only
 * those divisions' access counts, so an action is not offered on rows belonging to
 * divisions where the user lacks the role. See {@link DivisionScope} for single vs. list.
 *
 * While the organization's account is read-only, suspended or cancelled, every
 * `ALLOWED_ROLES` create/update/delete list is denied (see `access/writeRoles.ts`).
 */
export const useAllowedRoleChecker = (): AllowedRoleChecker => {
  const orgId = useSelector((state: AccessState) => state.organizations?.organization?.id);
  const claims = useSelector((state: AccessState) => state.user?.access?.data?.claims);
  // Packed-token fallback: when claims overflow (pk flag) the org entry may be missing from
  // the token; the org user doc (organizations/{orgId}/users/{uid}) carries the same role.
  const orgUserAccess = useSelector((state: AccessState) => state.user?.orgUser?.data?.access);
  const userOrgDivisions = useSelector(
    (state: AccessState) => state.divisions?.assigned?.data?.divisions,
  );
  const accountWritable = useAccountWritable();

  return useCallback(
    (allowedRoles, divScope) => {
      if (!allowedRoles) return true;
      if (!accountWritable && isWriteRoleList(allowedRoles)) return false;

      const userOrgAccess = resolveOrgAccess(claims, orgId, orgUserAccess);

      // Only the organization role can grant this; a division role never does.
      const orgOnly = () => hasAnyAllowedRole(allowedRoles, userOrgAccess, {});

      const forDivision = (divisionId: string) => {
        const division = userOrgDivisions?.[divisionId];
        return hasAnyAllowedRole(
          allowedRoles,
          userOrgAccess,
          division ? { [divisionId]: division } : {},
        );
      };

      if (Array.isArray(divScope)) {
        // `every` on an empty list is vacuously true, which would hand an org-wide record to any
        // division user; an empty scope is organization-only by definition.
        return divScope.length ? divScope.every(forDivision) : orgOnly();
      }

      if (divScope) return forDivision(divScope as string);

      return hasAnyAllowedRole(allowedRoles, userOrgAccess, userOrgDivisions ?? {});
    },
    [accountWritable, claims, orgId, orgUserAccess, userOrgDivisions],
  );
};

const useHasAnyAllowedRole = (
  allowedRoles?: readonly string[] | null,
  divScope?: DivisionScope,
): boolean => useAllowedRoleChecker()(allowedRoles, divScope);

export default useHasAnyAllowedRole;
